import { useState, useEffect } from 'react';
import { Star, ChevronRight, TrendingUp, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { API_URL } from '../lib/api-client';
import { toast } from 'sonner';

interface SponsoredProfile {
  _id: string;
  userId?: {
    _id: string;
  };
  firstName: string;
  lastName: string;
  age?: number;
  height?: string;
  city?: string;
  occupation?: string;
  photos?: any[];
  profilePhoto?: string;
  education?: string;
  religion?: string;
}

interface SponsoredProfilesListProps {
  onViewProfile: (profileId: string, isSponsored: boolean) => void;
  onViewAll?: () => void;
  maxProfiles?: number;
  variant?: 'card' | 'compact';
}

export function SponsoredProfilesList({
  onViewProfile,
  onViewAll,
  maxProfiles = 4,
  variant = 'card'
}: SponsoredProfilesListProps) {
  const [profiles, setProfiles] = useState<SponsoredProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchSponsoredProfiles();
  }, []);

  const fetchSponsoredProfiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/profiles/sponsored?limit=${maxProfiles}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sponsored profiles');
      }

      const data = await response.json();
      
      if (data.success) {
        setProfiles(data.data.profiles || []);
        setTotalCount(data.data.total || 0);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching sponsored profiles:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = async (profile: SponsoredProfile) => {
    const profileId = profile._id;
    
    try {
      // Deduct credit for viewing sponsored profile
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view profiles');
        return;
      }

      const response = await fetch(`${API_URL}/profiles/sponsored/${profileId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // Navigate to profile
        onViewProfile(profileId, true);
        
        // Show credit deduction message
        if (data.data.creditDeducted) {
          toast.success(`1 credit used. ${data.data.remainingCredits || 0} credits remaining`, {
            duration: 3000
          });
        }
      } else {
        toast.error(data.message || 'Failed to view profile');
      }
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error viewing sponsored profile:', error);
      }
      toast.error('Failed to view profile');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="size-5 rounded bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse" />
          <div className="h-5 bg-gray-200 rounded w-32 animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3 p-3 rounded-lg border border-gray-200">
              <div className="size-16 rounded-lg bg-gray-200 animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!profiles.length) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <TrendingUp className="size-4 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Sponsored Profiles</h3>
              <p className="text-white/80 text-xs">{totalCount} profiles boosting visibility</p>
            </div>
          </div>
          <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm text-xs">
            <Star className="size-3 mr-1 fill-white" />
            Featured
          </Badge>
        </div>
      </div>

      {/* Profiles List */}
      <div className="p-4 space-y-3">
        {profiles.map((profile) => {
          const photoUrl = profile.photos?.[0] 
            ? (typeof profile.photos[0] === 'string' ? profile.photos[0] : profile.photos[0]?.url)
            : profile.profilePhoto || `https://ui-avatars.com/api/?name=${profile.firstName}+${profile.lastName}&background=f43f5e&color=fff`;

          return (
            <div
              key={profile._id}
              onClick={() => handleProfileClick(profile)}
              className="flex gap-3 p-3 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all cursor-pointer group"
            >
              {/* Profile Photo */}
              <div className="relative flex-shrink-0">
                <div className="size-16 rounded-lg overflow-hidden border-2 border-purple-200">
                  <ImageWithFallback
                    src={photoUrl}
                    alt={`${profile.firstName} ${profile.lastName}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -top-1 -right-1 size-5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center border-2 border-white">
                  <Crown className="size-3 text-white" />
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="font-medium truncate">
                    {profile.firstName} {profile.lastName?.charAt(0)}.
                  </h4>
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 text-xs flex-shrink-0">
                    SPONSORED
                  </Badge>
                </div>
                <p className="text-xs text-gray-600 truncate">
                  {profile.age || 25} Yrs, {profile.height || "5'5\""} • {profile.city || 'India'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {profile.occupation || profile.education || 'Professional'}
                </p>
              </div>

              {/* Arrow Icon */}
              <div className="flex items-center">
                <ChevronRight className="size-5 text-gray-400 group-hover:text-purple-500 transition-colors" />
              </div>
            </div>
          );
        })}
      </div>

      {/* View All Button */}
      {totalCount > maxProfiles && onViewAll && (
        <div className="border-t border-gray-200 p-4">
          <Button
            onClick={onViewAll}
            variant="outline"
            className="w-full border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-300"
          >
            View All {totalCount} Sponsored Profiles
            <ChevronRight className="size-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}